import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button as MuiButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  configurationsCreateConfigurationMutation,
  configurationsDeleteConfigurationMutation,
  configurationsListConfigurationsOptions,
  configurationsPublishConfigurationMutation,
  configurationsUnpublishConfigurationMutation,
  useApi,
  useHeyApi,
} from "@platform/api";
import {
  Alert,
  Button,
  DataTableV2,
  TextField,
  Typography,
  useAgGridOptions,
} from "@platform/design-system";
import { useNotification, useStorage } from "@platform/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { Loader } from "../../components";
import { ROUTE_NAMES } from "../../constants";
import { extractErrorMessage } from "../../utils";
import { BrokerPartner } from "../Brokers/types";

import {
  brokerNamesFor,
  configurationLabel,
  filterConfigurations,
} from "./configurationRows";
import { DeleteConfigurationDialog } from "./DeleteConfigurationDialog";

import type { ConfigurationResponse } from "@platform/api";

// Search text and paging live in session storage so that opening a
// configuration and coming back lands on the list the user left.
const SESSION_STORAGE_KEY = "mpConfigsPageState";

type ConfigurationsPageState = {
  searchValue?: string;
};

const STATUS_COLORS: Record<
  string,
  "default" | "success" | "warning" | "error"
> = {
  draft: "default",
  published: "success",
  unpublished: "warning",
};

const StatusChip = ({ status }: { status: string }): React.JSX.Element => (
  <Chip
    label={status}
    color={STATUS_COLORS[status] ?? "default"}
    size="small"
    sx={{ textTransform: "capitalize" }}
  />
);

interface ActionsCellProps {
  data?: ConfigurationResponse;
  pendingActionId: string | null;
  onPublish: (id: string) => void;
  onUnpublish: (id: string) => void;
  onDelete: (config: ConfigurationResponse) => void;
}

const ActionsCell = ({
  data,
  pendingActionId,
  onPublish,
  onUnpublish,
  onDelete,
}: ActionsCellProps): React.JSX.Element | null => {
  if (!data) return null;
  const isActioning = pendingActionId === data.id;
  return (
    <Box display="flex" gap={1} alignItems="center" height="100%">
      {isActioning ? (
        <CircularProgress size={20} />
      ) : (
        <>
          {data.status !== "published" && (
            // TODO(ds-button): no DS variant for this status colour
            <MuiButton
              size="small"
              variant="outlined"
              color="success"
              onClick={() => onPublish(data.id)}
            >
              Publish
            </MuiButton>
          )}
          {data.status === "published" && (
            // TODO(ds-button): no DS variant for this status colour
            <MuiButton
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => onUnpublish(data.id)}
            >
              Unpublish
            </MuiButton>
          )}
          {!data.isDefault && (
            // TODO(ds-button): needs manual review — no DS low-emphasis (outlined) danger variant
            <MuiButton
              size="small"
              variant="outlined"
              color="error"
              onClick={() => onDelete(data)}
            >
              Delete
            </MuiButton>
          )}
        </>
      )}
    </Box>
  );
};

// One cell, one line: names joined for the row, the full list on hover.
const BrokersCell = ({ names }: { names: string[] }): React.JSX.Element => {
  if (names.length === 0) {
    return (
      <Box
        color="text.secondary"
        height="100%"
        display="flex"
        alignItems="center"
      >
        None
      </Box>
    );
  }

  const joined = names.join(", ");

  return (
    <Tooltip title={joined} placement="top-start">
      <Box
        height="100%"
        display="flex"
        alignItems="center"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
      >
        {joined}
      </Box>
    </Tooltip>
  );
};

export const ConfigurationsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { centroPortalClient } = useHeyApi();
  const { showError } = useNotification();

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [configToDelete, setConfigToDelete] =
    useState<ConfigurationResponse | null>(null);
  const { getStorageItem, setStorageItem } = useStorage("session");
  const [search, setSearch] = useState(
    () =>
      getStorageItem<ConfigurationsPageState>(SESSION_STORAGE_KEY)
        ?.searchValue ?? "",
  );
  const axiosInstance = useApi();

  const { data, isLoading, error } = useQuery(
    configurationsListConfigurationsOptions({ client: centroPortalClient }),
  );

  // Assignments come back as broker ids; names come from the same endpoint the
  // editor's assignment picker uses.
  const { data: allBrokers = [] } = useQuery<BrokerPartner[]>({
    queryKey: ["prepareManageBrokers"],
    queryFn: () =>
      axiosInstance
        .get<BrokerPartner[]>("/brokerportal/v1/prepareManageBrokers")
        .then((resp) => resp.data),
  });

  const brokerNamesById = useMemo(
    () => new Map(allBrokers.map((broker) => [broker.id, broker.name])),
    [allBrokers],
  );

  const rows = useMemo(
    () =>
      filterConfigurations(data?.configurations ?? [], search, brokerNamesById),
    [data?.configurations, search, brokerNamesById],
  );

  const handleCloseCreateOpen = (): void => {
    setCreateOpen(false);
    setNewName("");
  };

  const { mutate: createConfig, isPending: isCreating } = useMutation({
    ...configurationsCreateConfigurationMutation({
      client: centroPortalClient,
    }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({
        queryKey: [{ tags: ["configurations"] }],
      });
      handleCloseCreateOpen();
      navigate(`/${ROUTE_NAMES.MP_CONFIGS}/${created.id}`);
    },
    onError: (err) => showError(extractErrorMessage(err)),
  });

  const { mutate: publishConfig } = useMutation({
    ...configurationsPublishConfigurationMutation({
      client: centroPortalClient,
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [{ tags: ["configurations"] }],
      });
    },
    onError: (err) => showError(extractErrorMessage(err)),
    onSettled: () => setPendingActionId(null),
  });

  const { mutate: unpublishConfig } = useMutation({
    ...configurationsUnpublishConfigurationMutation({
      client: centroPortalClient,
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [{ tags: ["configurations"] }],
      });
    },
    onError: (err) => showError(extractErrorMessage(err)),
    onSettled: () => setPendingActionId(null),
  });

  const { mutate: deleteConfig, isPending: isDeleting } = useMutation({
    ...configurationsDeleteConfigurationMutation({
      client: centroPortalClient,
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [{ tags: ["configurations"] }],
      });
    },
    onError: (err) => showError(extractErrorMessage(err)),
    onSettled: () => {
      setPendingActionId(null);
      setConfigToDelete(null);
    },
  });

  const { resetPage, ...dataTableOptions } = useAgGridOptions({
    sessionStorageKey: SESSION_STORAGE_KEY,
  });

  const handleSearchChange = (value: string): void => {
    setSearch(value);
    // A search narrows the rows, so the page the user was on no longer means
    // the same thing.
    resetPage();
    setStorageItem(SESSION_STORAGE_KEY, {
      ...getStorageItem<ConfigurationsPageState>(SESSION_STORAGE_KEY),
      searchValue: value,
    });
  };

  const columnDefs: ColDef<ConfigurationResponse>[] = [
    {
      headerName: "Name",
      // Sorting and filtering follow the rendered "name - template" label.
      valueGetter: (params) =>
        params.data ? configurationLabel(params.data) : "",
      flex: 2,
      minWidth: 260,
      cellRenderer: (params: ICellRendererParams<ConfigurationResponse>) => (
        <Button
          variant="tertiary"
          size="sm"
          onClick={() =>
            navigate(`/${ROUTE_NAMES.MP_CONFIGS}/${params.data?.id}`)
          }
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      cellRenderer: (params: ICellRendererParams<ConfigurationResponse>) => (
        <StatusChip status={params.value as string} />
      ),
    },
    {
      headerName: "Brokers",
      flex: 3,
      minWidth: 240,
      valueGetter: (params) =>
        params.data
          ? brokerNamesFor(params.data, brokerNamesById).join(", ")
          : "",
      cellRenderer: (params: ICellRendererParams<ConfigurationResponse>) => (
        <BrokersCell
          names={
            params.data ? brokerNamesFor(params.data, brokerNamesById) : []
          }
        />
      ),
    },
    {
      headerName: "Actions",
      sortable: false,
      filter: false,
      width: 210,
      cellRenderer: (params: ICellRendererParams<ConfigurationResponse>) => (
        <ActionsCell
          data={params.data}
          pendingActionId={pendingActionId}
          onPublish={(id) => {
            setPendingActionId(id);
            publishConfig({ path: { id }, client: centroPortalClient });
          }}
          onUnpublish={(id) => {
            setPendingActionId(id);
            unpublishConfig({ path: { id }, client: centroPortalClient });
          }}
          onDelete={setConfigToDelete}
        />
      ),
    },
  ];

  if (isLoading) return <Loader />;

  return (
    <Box p={3} pt={12}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="header-large" component="h1">
          MP Configs
        </Typography>
        <Button
          variant="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create Configuration
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractErrorMessage(error, "Failed to load configurations.")}
        </Alert>
      )}

      <TextField
        value={search}
        onChange={(e) => handleSearchChange(e.target.value)}
        placeholder="Search by name, template or broker"
        size="sm"
        sx={{ mb: 2, width: 380 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      <DataTableV2
        columnDefs={columnDefs}
        rowData={rows}
        {...dataTableOptions}
      />

      <DeleteConfigurationDialog
        config={configToDelete}
        isDeleting={isDeleting}
        onCancel={() => setConfigToDelete(null)}
        onConfirm={(id) => {
          setPendingActionId(id);
          deleteConfig({ path: { id }, client: centroPortalClient });
        }}
      />

      <Dialog
        open={createOpen}
        onClose={isCreating ? undefined : handleCloseCreateOpen}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Configuration</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            disabled={isCreating}
            onClick={handleCloseCreateOpen}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!newName.trim() || isCreating}
            onClick={() =>
              createConfig({
                body: { name: newName.trim() },
                client: centroPortalClient,
              })
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
